import { createContext, useContext } from "react";

import { TableKey } from "./types";

/**
 * Stable layout values mounted once by `GroupedBody`. Theme colors and column
 * counts. Independent of `SelectionContext` so child-row layout consumers don't
 * re-render when selection state moves.
 */
export interface LayoutContextValue {
    summaryColumnCount: number;
    leadingCells: 1 | 2;
    divider: string;
    headerBackground: string;
    detailBackground: string;
    hoverBackground: string;
    onDetailRowNavigatePath?: (item: unknown) => string | undefined;
}

export const LayoutContext = createContext<LayoutContextValue | null>(null);

export function useLayoutContext(): LayoutContextValue {
    const ctx = useContext(LayoutContext);
    if (!ctx) {
        throw new Error("useLayoutContext must be used inside a <GroupedBody>.");
    }
    return ctx;
}

/**
 * Volatile child-row selection. Mounted only when `selection` is enabled.
 * Functions are stable references (memoized in `useSelection`) so individual
 * child rows can be `React.memo`-ed safely.
 */
export interface SelectionContextValue {
    isSelected: (item: unknown) => boolean;
    toggle: (item: unknown) => void;
    getDetailKey: (item: unknown) => TableKey;
}

export const SelectionContext = createContext<SelectionContextValue | null>(null);

/** Optional: returns `null` when selection isn't enabled. */
export function useSelectionContext(): SelectionContextValue | null {
    return useContext(SelectionContext);
}
